import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Delete, Edit, VisibilityOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, DocumentData, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import { YuvagaazMagazine } from "../../yuva_gaaz/yuvagaaz_data";

const EMPTY_FORM = {
  title: "",
  year: "",
  releaseDate: "",
};

function sortMagazines(items: YuvagaazMagazine[]) {
  return [...items].sort((itemA, itemB) => {
    const dateA = new Date(itemA.releaseDate || `${itemA.year}-01-01`).getTime();
    const dateB = new Date(itemB.releaseDate || `${itemB.year}-01-01`).getTime();
    return dateB - dateA;
  });
}

function isManagedMagazine(item: YuvagaazMagazine) {
  return Boolean(item.docId);
}

export default function ManageMagazinesTab() {
  const { showSnackbar } = useFeedback();
  const [items, setItems] = useState<YuvagaazMagazine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<YuvagaazMagazine | null>(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);

  async function fetchItems() {
    try {
      setIsLoading(true);
      const [legacySnapshot, managedSnapshot] = await Promise.all([
        getDocs(collection(firestore, "yuvagaaz")),
        getDocs(query(collection(firestore, "yuvagaaz_magazines"), orderBy("releaseDate", "desc"))),
      ]);

      const legacyItems = legacySnapshot.docs
        .map((itemDoc) => itemDoc.data() as DocumentData)
        .flatMap((itemDoc) => itemDoc?.yuvagaazList || [])
        .map((item: any, index: number) => ({
          title: item.title || `Yuvagaaz Magazine ${item.year}`,
          year: String(item.year || ""),
          pdfUrl: item.pdfUrl || item.path || "",
          coverImage: item.coverImage || "",
          releaseDate: item.releaseDate || `${item.year || "2000"}-01-01`,
          createdAt: "",
          updatedAt: "",
          legacyKey: `${item.year || "legacy"}-${item.title || index}`,
        }));

      const managedItems = managedSnapshot.docs.map((itemDoc) => ({
        docId: itemDoc.id,
        ...(itemDoc.data() as Omit<YuvagaazMagazine, "docId">),
      }));

      const fetchedItems = [...managedItems, ...legacyItems];
      const existingYears = new Set(fetchedItems.map((item) => item.year));
      const defaultStaticItems: YuvagaazMagazine[] = [
        {
          title: "Yuvagaaz Magazine 2026",
          year: "2026",
          pdfUrl: "/pdf/yuvagaaz_2026.pdf",
          coverImage: "/images/yuvagaazEditors/yuvagaaz_2026.jpg",
          releaseDate: "2026-01-01",
          createdAt: "",
          updatedAt: "",
          legacyKey: "static-2026",
        },
        {
          title: "Yuvagaaz Magazine 2025",
          year: "2025",
          pdfUrl: "/pdf/yuvagaaz_2025.pdf",
          coverImage: "/images/yuvagaazEditors/yuvagaaz_2025.jpg",
          releaseDate: "2025-01-01",
          createdAt: "",
          updatedAt: "",
          legacyKey: "static-2025",
        },
      ].filter((def) => !existingYears.has(def.year));

      setItems(sortMagazines([...fetchedItems, ...defaultStaticItems]));
    } catch (error) {
      console.error("Failed to load magazines", error);
      showSnackbar("Failed to load magazines");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(item: YuvagaazMagazine) {
    setEditingItem(item);
    setFormValues({
      title: item.title || "",
      year: item.year || "",
      releaseDate: item.releaseDate || "",
    });
    setSelectedCoverImage(null);
    setSelectedPdfFile(null);
  }

  async function handleUpdate() {
    if (!editingItem?.docId) {
      return;
    }

    try {
      let coverImage = editingItem.coverImage || "";
      let pdfUrl = editingItem.pdfUrl;

      if (selectedCoverImage) {
        const coverRef = ref(storage, `yuvagaaz/magazine-covers/${Date.now()}-${selectedCoverImage.name}`);
        await uploadBytes(coverRef, selectedCoverImage);
        coverImage = await getDownloadURL(coverRef);
      }

      if (selectedPdfFile) {
        const pdfRef = ref(storage, `yuvagaaz/magazines/${Date.now()}-${selectedPdfFile.name}`);
        await uploadBytes(pdfRef, selectedPdfFile);
        pdfUrl = await getDownloadURL(pdfRef);
      }

      await updateDoc(doc(firestore, "yuvagaaz_magazines", editingItem.docId), {
        title: formValues.title.trim(),
        year: formValues.year.trim(),
        releaseDate: formValues.releaseDate.trim() || `${formValues.year.trim()}-01-01`,
        coverImage,
        pdfUrl,
        updatedAt: new Date().toISOString(),
      });

      showSnackbar("Magazine updated successfully");
      setEditingItem(null);
      setFormValues(EMPTY_FORM);
      setSelectedCoverImage(null);
      setSelectedPdfFile(null);
      fetchItems();
    } catch (error) {
      console.error("Failed to update magazine", error);
      showSnackbar("Failed to update magazine");
    }
  }

  async function handleDelete(item: YuvagaazMagazine) {
    if (!item.docId) {
      return;
    }

    if (!window.confirm(`Delete "${item.title}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, "yuvagaaz_magazines", item.docId));
      showSnackbar("Magazine deleted successfully");
      fetchItems();
    } catch (error) {
      console.error("Failed to delete magazine", error);
      showSnackbar("Failed to delete magazine");
    }
  }

  if (isLoading) {
    return <Box py={6} textAlign="center"><Typography>Loading magazines...</Typography></Box>;
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={item.docId || (item as any).legacyKey || `${item.year}-${item.title}`}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Box sx={{ width: "100%", height: 240, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", borderBottom: "1px solid rgba(56, 136, 194, 0.12)", p: 2 }}>
                {item.coverImage ? (
                  <Box component="img" src={item.coverImage} alt={item.title} sx={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center center" }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">No cover image</Typography>
                )}
              </Box>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary">{item.year}</Typography>
                {item.releaseDate ? <Typography variant="body2" color="text.secondary">{new Date(item.releaseDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</Typography> : null}
                {!isManagedMagazine(item) ? (
                  <Typography variant="body2" color="text.secondary">
                    Legacy magazine already available on the website
                  </Typography>
                ) : null}
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button variant="outlined" startIcon={<VisibilityOutlined />} onClick={() => window.open(item.pdfUrl, "_blank", "noopener,noreferrer")}>View</Button>
                  {isManagedMagazine(item) ? <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(item)}>Edit</Button> : null}
                  {isManagedMagazine(item) ? <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(item)}>Delete</Button> : null}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingItem)} onClose={() => setEditingItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Magazine</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <TextField fullWidth label="Title" value={formValues.title} onChange={(event) => setFormValues((prev) => ({ ...prev, title: event.target.value }))} />
            <TextField fullWidth label="Year" value={formValues.year} onChange={(event) => setFormValues((prev) => ({ ...prev, year: event.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))} inputProps={{ maxLength: 4 }} />
            <TextField fullWidth label="Release Date" type="date" value={formValues.releaseDate} onChange={(event) => setFormValues((prev) => ({ ...prev, releaseDate: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth>
              <FormLabel>Upload New Cover Image</FormLabel>
              <input type="file" accept="image/*" style={{ border: "1px solid rgb(181, 176, 176)", width: "96%", paddingLeft: 8, paddingRight: 8, paddingTop: 16, paddingBottom: 16, borderRadius: 5, marginTop: 8 }} onChange={(event) => setSelectedCoverImage(event.target.files?.[0] || null)} />
            </FormControl>
            <FormControl fullWidth>
              <FormLabel>Upload New PDF File</FormLabel>
              <input type="file" accept="application/pdf" style={{ border: "1px solid rgb(181, 176, 176)", width: "96%", paddingLeft: 8, paddingRight: 8, paddingTop: 16, paddingBottom: 16, borderRadius: 5, marginTop: 8 }} onChange={(event) => setSelectedPdfFile(event.target.files?.[0] || null)} />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingItem(null)} variant="outlined">Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
